<template>
  <div>
    <b-card class="product-card h-100">
      <!-- 主图片显示区域 -->
      <div class="main-image-container">
        <img 
          :src="currentImage" 
          :alt="product.name" 
          class="main-image"
          @error="handleImageError"
        />
      </div>
      
      <!-- 缩略图区域 -->
      <div class="thumbnails-container" v-if="availableImages.length > 0">
        <div 
          v-for="(image, index) in availableImages" 
          :key="index"
          class="thumbnail-wrapper"
          :class="{ active: currentImageIndex === index }"
          @click="selectImage(index)"
        >
          <img 
            :src="getImageUrl(image)" 
            :alt="`${product.name} 图片 ${index + 1}`" 
            class="thumbnail-image"
            @error="handleThumbnailError"
          />
        </div>
      </div>

      <b-card-body>
        <b-card-title class="text-primary">{{ product.name }}</b-card-title>
        <b-card-sub-title class="mb-1 text-muted">{{ product.category }}</b-card-sub-title>
        <div class="cas-number mb-2">
          <small class="text-info font-weight-bold">CAS: {{ product.cas || '-' }}</small>
        </div>
        <b-card-text class="product-description">
          <span class="font-weight-bold">品牌/产地：</span>{{ product.brand_origin || '-' }}
        </b-card-text>

      </b-card-body>
      
      <template #footer>
        <div class="d-flex justify-content-between">
          <b-button variant="outline-primary" size="sm" @click="showDetails">
            <b-icon icon="eye" class="mr-1"></b-icon>
            查看详情
          </b-button>
          <b-button variant="primary" size="sm" @click="inquire">
            <b-icon icon="chat-dots" class="mr-1"></b-icon>
            询价
          </b-button>
        </div>
      </template>
    </b-card>
    
    <!-- 询价弹窗 -->
    <InquiryModal ref="inquiryModal" :product="product" />
  </div>
</template>

<script>
import InquiryModal from './InquiryModal.vue'
export default {
  name: 'ProductCard',
  components: {
    InquiryModal
  },
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentImageIndex: 0,
      fallbackImage: 'https://via.placeholder.com/300x200?text=暂无图片'
    }
  },
  computed: {
    availableImages() {
      // 如果有images数组，使用它
      if (this.product.images && this.product.images.length > 0) {
        return this.product.images
      }
      // 否则，如果有image_url，将其作为单个图片数组
      if (this.product.image_url || this.product.image) {
        return [this.product.image_url || this.product.image]
      }
      return []
    },
    currentImage() {
      if (this.availableImages.length > 0) {
        return this.getImageUrl(this.availableImages[this.currentImageIndex])
      }
      return this.fallbackImage
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToActiveThumbnail()
    })
  },
  methods: {
    // 获取完整的图片URL
    getImageUrl(imageUrl) {
      if (!imageUrl) return ''
      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http')) {
        return imageUrl
      }
      // 直接返回后端提供的相对路径
      return imageUrl
    },
    selectImage(index) {
      this.currentImageIndex = index
      this.$nextTick(() => {
        this.scrollToActiveThumbnail()
      })
    },
    scrollToActiveThumbnail() {
      const container = this.$el.querySelector('.thumbnails-container')
      const activeThumbnail = this.$el.querySelector('.thumbnail-wrapper.active')
      
      if (container && activeThumbnail) {
        const containerWidth = container.offsetWidth
        const thumbnailLeft = activeThumbnail.offsetLeft
        const thumbnailWidth = activeThumbnail.offsetWidth
        
        // 计算滚动位置，使选中的缩略图居中
        const scrollLeft = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2)
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        })
      }
    },
    handleImageError(event) {
      event.target.src = this.fallbackImage
    },
    handleThumbnailError(event) {
      event.target.src = this.fallbackImage
    },
    showDetails() {
      this.$emit('show-details', this.product)
    },
    inquire() {
      this.$refs.inquiryModal.show()
    }
  }
}
</script>

<style scoped>
.product-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* 主图片容器 */
.main-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  object-position: center;
  transition: transform 0.3s ease;
}

.main-image:hover {
  transform: scale(1.05);
}

/* 缩略图容器 */
.thumbnails-container {
  display: flex;
  gap: 8px;
  padding: 10px;
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  align-items: center;
  scroll-behavior: smooth;
}

.thumbnails-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.thumbnail-wrapper {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.thumbnail-wrapper:hover {
  border-color: #007bff;
  transform: scale(1.1);
}

.thumbnail-wrapper.active {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.card-footer {
  background-color: transparent;
  border-top: 1px solid rgba(0, 0, 0, 0.125);
}

/* 产品描述文字样式 */
.product-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  height: 2.8em; /* 固定2行高度 */
  min-height: 2.8em; /* 确保最小高度 */
}
</style>